import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useResolveRequestHook } from '@/modules/shared/hooks/use-resolve-request.hook'
import { useAuthStore } from '@/modules/shared/stores/auth.store'
import { useMainStore } from '@/modules/shared/stores/main.store'
import { useOrderFormComponentStoreActions } from '../stores/order-form-component.store'
import { useOrderFormStore, useOrderFormStoreActions } from '../stores/order-form.store'
import { ORDER_SEARCH_PARAMS } from '../constants/order-search-filter.constants'
import type { TPayloadToOrderFormSearchInputComponent } from '../services/order-search.service'
import { orderSearchService } from '../services/order-search.service'
import type { Order } from '../types/order.type'
import { useOrderMutations } from './use-order-mutations.hook'
import { useSaveOrderWhenHaveInStorage } from './use-save-order-when-back-system.hook'
import { queryClient } from '@/modules/shared/lib/query-client'
import { QUERY_KEYS } from '@/modules/shared/constants/query-keys.contant'
import { useOrdersSearchStore } from '../stores/order-search.store'
import { useOrderSearchData } from '../data/order-search.data'

const FRONTEND_TO_API_MAP: Record<string, string> = {
  codigo: 'id_pedido',
  data_emissao: 'data_emissao',
  cliente: 'c.razao_social_nome',
  cnpjcpf: 'c.cnpjcpf',
  status: 's.sigla'
}

export const useOrderSearch = () => {
  const [typeConfirmationModal, setTypeConfirmationModal] = useState<string>('')
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [isLoadingSearch, setIsLoadingSearch] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const currentSearchRef = useRef<TPayloadToOrderFormSearchInputComponent | null>(null)

  const ordersRef = useRef<Order[]>([])
  const totalRecordsRef = useRef<number>(0)
  const hasMoreRef = useRef<boolean>(false)
  const currentPageRef = useRef<number>(1)
  const initializedRef = useRef<boolean>(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const [showTodayOrders, setShowTodayOrders] = useState(() => searchParams.get('today') === 'true')

  const { orders, filters, actions: { setOrders, setFilters } } = useOrdersSearchStore()
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { setConfirmation, setCloseConfirmation, setIsLoading } = useMainStore().actions
  const { checkStatusOnRequest } = useResolveRequestHook()
  const { deleteOrder, transmitOrder, isLoadingMutations, isLoadingDeleteOrder } = useOrderMutations()
  const { checkIfOrderInStorage } = useSaveOrderWhenHaveInStorage()
  useOrderSearchData()
  const { orderSelected } = useOrderFormStore()
  const { setUrlOrderPdf } = useOrderFormComponentStoreActions
  const { setOperationType, setOrderSelected } = useOrderFormStoreActions()

  const buildPayloadFromQueryParams = useCallback((): TPayloadToOrderFormSearchInputComponent | undefined => {
    const showToday = searchParams.get('today') === 'true'
    if (showToday) {
      const today = new Date().toLocaleDateString('pt-BR').split('/').reverse().join('-')
      return {
        payload: {
          filters: [{
            filter: 'data_emissao',
            filterInput: `${today}/${today}`,
            apiParam: 'data_emissao'
          }]
        }
      }
    }

    const filters: { filter: string; filterInput: string; apiParam?: string }[] = []

    // Mapeamento entre parâmetros de URL e nomes de filtro
    const paramToFilterMap: Record<string, string> = {
      dataInicio: 'data_emissao',
      codigo: 'codigo',
      cliente: 'cliente',
      status: 'status',
      cnpjcpf: 'cnpjcpf'
    }

    let dataFilter = ''
    for (const [param, value] of searchParams.entries()) {
      if (param === 'dataInicio') {
        const dataFim = searchParams.get('dataFim') || ''
        if (value && dataFim) {
          dataFilter = `${value}/${dataFim}`
        }
      } else if (param in paramToFilterMap) {
        if (value) {
          const filterKey = paramToFilterMap[param]
          filters.push({
            filter: filterKey,
            filterInput: value,
            apiParam: FRONTEND_TO_API_MAP[filterKey]
          })
        }
      }
    }

    if (dataFilter) {
      filters.push({
        filter: 'data_emissao',
        filterInput: dataFilter,
        apiParam: 'data_emissao'
      })
    }

    return filters.length > 0 ? { payload: { filters } } : undefined
  }, [searchParams])

  const updateQueryParams = useCallback((payload?: TPayloadToOrderFormSearchInputComponent, showToday = false) => {
    const newParams = new URLSearchParams()
    if (showToday) newParams.set('today', 'true')

    if (payload?.payload?.filters) {
      for (const filter of payload.payload.filters) {
        if (filter.filterInput.trim() !== '') {
          if (filter.filter === 'data_emissao') {
            const [dataInicio, dataFim] = filter.filterInput.split('/')
            if (dataInicio) newParams.set('dataInicio', dataInicio)
            if (dataFim) newParams.set('dataFim', dataFim)
          } else {
            newParams.set(filter.filter, filter.filterInput)
          }
        }
      }
    }
    setSearchParams(newParams, { replace: true })
  }, [setSearchParams])

  const resetSearch = useCallback(() => {
    ordersRef.current = []
    setOrders([])
    setIsLoadingSearch(false)
    setIsLoadingMore(false)
    hasMoreRef.current = false
    setHasMore(false)
    totalRecordsRef.current = 0
    setTotalRecords(0)
    currentPageRef.current = 1
    setCurrentPage(1)
  }, [setOrders])

  const resetSearchCompletely = useCallback(() => {
    resetSearch()
    currentSearchRef.current = null
  }, [resetSearch])

  const handleSubmitSearchOrderWithPagination = useCallback(async (payload?: TPayloadToOrderFormSearchInputComponent) => {
    setIsLoading(true) // Ativar loading global
    setIsLoadingSearch(true)
    resetSearch()

    currentSearchRef.current = payload || null
    try {
      const result = await orderSearchService.searchInitialOrders(payload)
      ordersRef.current = result.orders
      setOrders(result.orders)
      totalRecordsRef.current = result.totalRecords
      setTotalRecords(result.totalRecords)
      hasMoreRef.current = result.hasMore
      setHasMore(result.hasMore)
      currentPageRef.current = 2
      setCurrentPage(2)
    } catch (error) {
      console.error('❌ Erro na busca de pedidos:', error)
      toast.error('Erro ao buscar pedidos. Tente novamente.')
      resetSearch()
    } finally {
      setIsLoading(false) // Desativar loading global
      setIsLoadingSearch(false)
    }
  }, [resetSearch, setOrders, setIsLoading])

  const loadMoreOrders = useCallback(async () => {
    if (isLoadingMore || !hasMoreRef.current) return
    setIsLoadingMore(true)

    try {
      const result = await orderSearchService.loadMoreOrders(
        currentSearchRef.current || undefined,
        currentPageRef.current,
        ordersRef.current.length,
        totalRecordsRef.current
      )

      if (result.orders.length > 0) {
        const newOrders = [...ordersRef.current, ...result.orders]
        ordersRef.current = newOrders
        setOrders(newOrders)
        currentPageRef.current += 1
        setCurrentPage(currentPageRef.current)
      }
      hasMoreRef.current = result.hasMore
      setHasMore(result.hasMore)
    } catch (error) {
      console.error('❌ Erro ao carregar mais pedidos:', error)
      toast.error('Erro ao carregar mais pedidos')
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, setOrders])

  const handleBackPage = () => {
    setFilters(undefined)
    resetSearch()
    navigate('/')
  }

  const handleRemoveFilter = useCallback((filterType: string) => {
    const newParams = new URLSearchParams(searchParams)

    // Mapeamento reverso: filtro para parâmetros de URL
    const filterToParamMap: Record<string, string[]> = {
      data_emissao: ['dataInicio', 'dataFim'],
      codigo: ['codigo'],
      cliente: ['cliente'],
      status: ['status'],
      cnpjcpf: ['cnpjcpf']
    }

    const paramsToRemove = filterToParamMap[filterType] || [filterType]
    for (const param of paramsToRemove) {
      newParams.delete(param)
    }

    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  const handleSubmitSearchOrder = async ({ payload }: TPayloadToOrderFormSearchInputComponent) => {
    resetSearch()
    updateQueryParams({ payload }, false)

    // Converter filtros para parâmetros da API
    const apiPayload = {
      payload: {
        filters: payload.filters.map(filter => ({
          ...filter,
          apiParam: FRONTEND_TO_API_MAP[filter.filter] || filter.filter
        }))
      }
    }

    // Atualizar estado dos filtros
    const filterState = {
      codigo: payload.filters.find(f => f.filter === 'codigo')?.filterInput || '',
      cliente: payload.filters.find(f => f.filter === 'cliente')?.filterInput || '',
      status: payload.filters.find(f => f.filter === 'status')?.filterInput || '',
      cnpjcpf: payload.filters.find(f => f.filter === 'cnpjcpf')?.filterInput || '',
      dataInicio: payload.filters.find(f => f.filter === 'data_emissao')?.filterInput?.split('/')[0] || '',
      dataFim: payload.filters.find(f => f.filter === 'data_emissao')?.filterInput?.split('/')[1] || '',
    }

    setFilters(filterState)
    await handleSubmitSearchOrderWithPagination(apiPayload)
  }

  const handleToggleTodayOrders = useCallback(async (show: boolean) => {
    if (show === showTodayOrders) return

    setShowTodayOrders(show)
    updateQueryParams(undefined, show)
    resetSearchCompletely()
    queryClient.removeQueries([QUERY_KEYS.ORDERS])

    if (show) {
      const today = new Date().toLocaleDateString('pt-BR').split('/').reverse().join('-')
      const payload = {
        payload: {
          filters: [{
            filter: 'data_emissao',
            filterInput: `${today}/${today}`,
            apiParam: 'data_emissao'
          }]
        }
      }
      await handleSubmitSearchOrderWithPagination(payload)
      setFilters({
        dataInicio: today,
        dataFim: today,
        codigo: '',
        cliente: '',
        status: '',
        cnpjcpf: ''
      })
    } else {
      await handleSubmitSearchOrderWithPagination()
      setFilters({})
    }
  }, [showTodayOrders, updateQueryParams, resetSearchCompletely, setFilters, handleSubmitSearchOrderWithPagination])

  const handleStartNewOrder = () => {
    setOperationType('add')
    navigate('/pedido/add')
  }

  const handleEditOrder = () => {
    if (['10', '23'].includes(orderSelected.fkStatus)) {
      setOperationType('edit')
      navigate(`/pedido/edit/${orderSelected?.idPedidoCel}`)
      return
    }
    toast.info('Este pedido não pode ser alterado!')
  }

  const handleViewOrder = (orderId: number) => {
    const selectOrder = orders.find(order => order.idPedidoCel === orderId) || orderSelected
    setOrderSelected(selectOrder)
    if (selectOrder?.fkStatus !== '23') {
      setOperationType('view')
      navigate(`/pedido/view/${selectOrder?.idPedidoCel}`)
    }
  }

  const handleReplicateOrder = () => {
    if (orderSelected.fkStatus === '23') {
      toast.info('Não é possível replicar rascunhos!')
      return
    }
    if (user?.id && orderSelected?.fkUsuarioCel !== user?.id) {
      toast.info('Não é possível replicar pedidos de outro usuário!')
      return
    }
    setTypeConfirmationModal('replicate')
    setIsOpenModal(true)
  }

  const handleLoadOrderPdf = () => {
    if (orderSelected.fkStatus !== '23') {
      setTypeConfirmationModal('pdf')
      setIsOpenModal(true)
      return
    }
    toast.info('Não é possível gerar PDF de rascunhos!')
  }

  const handleSelectActionToStart = (actionType: string) => {
    const actions: Record<string, () => void> = {
      sharePdf: handleLoadOrderPdf,
      transmit: handleOrderTransmit,
      replicate: handleReplicateOrder,
      delete: handleOrderDelete,
      viewOrder: () => handleViewOrder(0),
      edit: handleEditOrder,
      add: handleStartNewOrder,
    }
    actions[actionType]?.()
  }

  const handleOrderDelete = () => {
    if (['10', '23'].includes(orderSelected.fkStatus)) {
      setConfirmation({
        isOpen: true,
        message: 'Deseja excluir este pedido?',
        onClose: setCloseConfirmation,
        onConfirm: async () => {
          await deleteOrder({ orderId: orderSelected.idPedidoCel })
          
          // Remover o pedido da lista local após exclusão bem-sucedida
          const updatedOrders = ordersRef.current.filter(order => order.idPedidoCel !== orderSelected.idPedidoCel)
          ordersRef.current = updatedOrders
          setOrders(updatedOrders)
          
          // Atualizar o contador de registros
          totalRecordsRef.current = Math.max(0, totalRecordsRef.current - 1)
          setTotalRecords(totalRecordsRef.current)
          
          setCloseConfirmation()
        },
        title: 'Confirmação de exclusão',
      })
      return
    }
    toast.info('Este pedido não pode ser excluído!')
  }

const handleOrderTransmit = () => {
 if (orderSelected.fkStatus === '10') {
   setConfirmation({
     isOpen: true,
     message: 'Deseja transmitir este pedido?',
     onClose: setCloseConfirmation,
     onConfirm: async () => {
       await transmitOrder({ payload: { orderId: orderSelected.idPedidoCel.toString() } })
       
       const updatedOrders = ordersRef.current.map(order => {
         if (order.idPedidoCel === orderSelected.idPedidoCel) {
           return {
             ...order,
             fkStatus: '9',
             status: 'Importado',
             statusImg: 'MdFactory',
             statusSigla: 'TR',
           }
         }
         return order
       })

       ordersRef.current = updatedOrders
       setOrders(updatedOrders)
       setCloseConfirmation()
     },
   })
 }
 else if (orderSelected.fkStatus === '23') {
   toast.info('Não é possível transmitir rascunhos!')
 }
}

  const handleSelectOrderToStartActions = (orderSelectedFn: Order) => {
    setOrderSelected(orderSelected?.idPedido === orderSelectedFn.idPedido ? undefined : orderSelectedFn)
  }

  const handleCloseModal = () => {
    setIsOpenModal(false)
    setUrlOrderPdf(undefined)
  }
useEffect(() => {
  const initialize = async () => {
    if (initializedRef.current) return

    await Promise.all([
      setOrderSelected(undefined),
      checkIfOrderInStorage()
    ])

    const payload = buildPayloadFromQueryParams()
    await handleSubmitSearchOrderWithPagination(payload)

    initializedRef.current = true
  }

  initialize().catch(/* tratamento de erro */)
}, [])

  useEffect(() => {
    if (!initializedRef.current) return

    const payload = buildPayloadFromQueryParams()
    const showToday = searchParams.get('today') === 'true'
    setShowTodayOrders(showToday)

    const currentPayload = currentSearchRef.current
    const payloadChanged = !currentPayload || !payload
      ? currentPayload !== payload
      : JSON.stringify(currentPayload) !== JSON.stringify(payload)

    if (payloadChanged) {
      resetSearch()
      handleSubmitSearchOrderWithPagination(payload)
    }
  }, [searchParams])

  useEffect(() => {
    if (isLoadingSearch) setIsLoading(true)
    else setIsLoading(false)
  }, [isLoadingSearch, setIsLoading])

  return {
    isOpenModal,
    orders,
    orderSelected,
    isLoading: isLoadingSearch,
    isTransmiting: isLoadingMutations,
    isLoadingDeleteOrder,
    handleBackPage,
    handleCloseModal,
    typeConfirmationModal,
    handleSubmitSearchOrder,
    handleSelectActionToStart,
    ORDER_SEARCH_PARAMS,
    handleOrderDelete,
    handleSelectOrderToStartActions,
    user,
    handleViewOrder,
    setShowTodayOrders: handleToggleTodayOrders,
    showTodayOrders,
    handleRemoveFilter,
    isLoadingMore,
    hasMore: hasMoreRef.current,
    totalRecords: totalRecordsRef.current,
    loadMoreOrders,
    resetSearch,
    resetSearchCompletely,
  }
}